import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Add, Delete, Edit, PictureAsPdf, Print, Save, Visibility } from "@mui/icons-material";
import {
  createActivityCatalog,
  createClient,
  deleteClient,
  fetchClientFiche,
  fetchActivityCatalog,
  fetchClients,
  updateClient,
} from "../services/owdedApi";
import { BASE_URL } from "../services/constant";
import { dateOnly, money, percent } from "./format";
import { EmptyState, Modal, Pagination, StatusPill } from "./ui";
import { paginateRows, PAGE_SIZE } from "./listUtils";
import { useAuth } from "../context/AuthContext";

const initialForm = {
  last_name: "",
  first_name: "",
  sex: "M",
  phone: "",
  address: "",
  commune: "",
  activity_ids: [],
  photo_client_url: "",
  business_photo_url: "",
  identity_card_url: "",
  photo_client: null,
  business_photo: null,
  identity_card: null,
  estimated_daily_revenue: 0,
  estimated_monthly_revenue: 0,
  estimated_monthly_charges: 0,
  existing_working_capital: 0,
  risk_score: 0,
};

function ClientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [activityCatalog, setActivityCatalog] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [newActivity, setNewActivity] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [ficheOpen, setFicheOpen] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  const ficheRef = useRef(null);

  const load = async () => {
    const [clientRows, activities] = await Promise.all([fetchClients(), fetchActivityCatalog()]);
    setClients(clientRows);
    setActivityCatalog(activities);
  };

  useEffect(() => {
    load().catch(() => setNotice("Impossible de charger les clients."));
  }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return clients.filter((client) =>
      [client.generated_client_id, client.client_number, client.first_name, client.last_name, client.full_name, client.phone, client.activities]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [clients, search]);

  const pagination = useMemo(() => paginateRows(filtered, page, PAGE_SIZE), [filtered, page]);
  const isDirectorGeneral = user?.role_code === "DIRECTEUR_GENERAL";
  const monthlyRevenue = useMemo(
    () => Number(Number(form.estimated_daily_revenue || 0) * 26).toFixed(2),
    [form.estimated_daily_revenue]
  );

  useEffect(() => {
    setPage(1);
  }, [search, clients.length]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = (client) => {
    const ids = String(client.activity_ids || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    setEditing(client);
    setForm({
      ...initialForm,
      ...client,
      activity_ids: ids,
      photo_client: null,
      business_photo: null,
      identity_card: null,
    });
    setModalOpen(true);
  };

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const toggleActivity = (id) => {
    const value = String(id);
    setForm((current) => ({
      ...current,
      activity_ids: current.activity_ids.includes(value)
        ? current.activity_ids.filter((item) => item !== value)
        : [...current.activity_ids, value],
    }));
  };

  const addActivity = async () => {
    const name = newActivity.trim();
    if (!name) return;
    await createActivityCatalog({ name });
    setNewActivity("");
    setActivityCatalog(await fetchActivityCatalog());
  };

  const toFormData = () => {
    const data = new FormData();
    const fullName = `${form.last_name || ""} ${form.first_name || ""}`.trim();
    const selectedNames = activityCatalog
      .filter((activity) => form.activity_ids.includes(String(activity.id)))
      .map((activity) => activity.name);

    Object.entries({
      last_name: form.last_name || "",
      first_name: form.first_name || "",
      full_name: fullName,
      sex: form.sex || "M",
      phone: form.phone || "",
      address: form.address || "",
      commune: form.commune || "",
      main_activity: selectedNames.join(", "),
      activities: JSON.stringify(form.activity_ids),
      estimated_daily_revenue: form.estimated_daily_revenue || 0,
      estimated_monthly_revenue: monthlyRevenue,
      estimated_monthly_charges: form.estimated_monthly_charges || 0,
      existing_working_capital: form.existing_working_capital || 0,
      risk_score: Math.min(Math.max(Number(form.risk_score || 0), 0), 10),
      photo_client_url: form.photo_client_url || "",
      business_photo_url: form.business_photo_url || "",
      identity_card_url: form.identity_card_url || "",
    }).forEach(([key, value]) => data.append(key, value));

    if (form.photo_client) data.append("photo_client", form.photo_client);
    if (form.business_photo) data.append("business_photo", form.business_photo);
    if (form.identity_card) data.append("identity_card", form.identity_card);

    return data;
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = toFormData();

    if (editing) {
      await updateClient(editing.id, payload);
      setNotice("Client modifie.");
    } else {
      await createClient(payload);
      setNotice("Client cree avec ID genere.");
    }
    setModalOpen(false);
    await load();
  };

  const remove = async (client) => {
    if (!window.confirm(`Supprimer ${client.full_name} ?`)) return;
    await deleteClient(client.id);
    setNotice("Client supprime logiquement.");
    await load();
  };

  const assetUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE_URL}${url}`;
  };

  const openFiche = async (client) => {
    setNotice("");
    setFicheOpen(true);
    setFiche(null);
    try {
      setFiche(await fetchClientFiche(client.id));
    } catch (err) {
      setNotice("Impossible de charger la fiche client.");
      setFicheOpen(false);
    }
  };

  const buildClientPdf = async () => {
    if (!ficheRef.current || !fiche?.client) return null;
    const canvas = await html2canvas(ficheRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
    });
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const imageWidth = pageWidth - margin * 2;
    const imageHeight = (canvas.height * imageWidth) / canvas.width;
    let heightLeft = imageHeight;
    let position = margin;
    const image = canvas.toDataURL("image/png");

    pdf.addImage(image, "PNG", margin, position, imageWidth, imageHeight);
    heightLeft -= pageHeight - margin * 2;

    while (heightLeft > 0) {
      position -= pageHeight - margin * 2;
      pdf.addPage();
      pdf.addImage(image, "PNG", margin, position, imageWidth, imageHeight);
      heightLeft -= pageHeight - margin * 2;
    }

    return pdf;
  };

  const previewPdf = async () => {
    const pdf = await buildClientPdf();
    if (pdf) window.open(pdf.output("bloburl"), "_blank");
  };

  const printPdf = async () => {
    const pdf = await buildClientPdf();
    if (!pdf) return;
    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  };

  return (
    <main className="page-content management-page">
      <div className="page-head">
        <div>
          <h1>Clients</h1>
          <p>ID genere, pieces jointes, activites cataloguees et documents de terrain.</p>
        </div>
      </div>
      {notice && <p className="notice">{notice}</p>}

      <section className="panel data-panel">
        <div className="table-toolbar">
          <input placeholder="Recherche avancee..." value={search} onChange={(event) => setSearch(event.target.value)} />
          <button className="primary-btn" type="button" onClick={openCreate}><Add /> Ajouter</button>
        </div>
        {filtered.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Client</th>
                <th>Contact</th>
                <th>Activites</th>
                <th>Revenus</th>
                <th>Pieces</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagination.rows.map((client) => (
                <tr key={client.id}>
                  <td>{client.generated_client_id || client.client_number}</td>
                  <td>{client.full_name}<br /><small>{client.sex} - {client.commune}</small></td>
                  <td>{client.phone}<br /><small>{client.address}</small></td>
                  <td>{client.activities || "-"}</td>
                  <td>{money(client.estimated_monthly_revenue, "CDF")}</td>
                  <td>
                    <span className="doc-count">
                      {[client.photo_client_url, client.identity_card_url, client.business_photo_url].filter(Boolean).length}/3
                    </span>
                  </td>
                  <td><StatusPill value={client.client_status} /></td>
                  <td>
                    <div className="table-actions">
                      <button className="icon-action" type="button" onClick={() => openFiche(client)} aria-label="Voir fiche"><Visibility /></button>
                      <button className="icon-action" type="button" onClick={() => openEdit(client)} aria-label="Modifier"><Edit /></button>
                      {isDirectorGeneral && <button className="icon-action danger" type="button" onClick={() => remove(client)} aria-label="Supprimer"><Delete /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState />}
        <Pagination
          page={pagination.safePage}
          totalPages={pagination.totalPages}
          totalItems={filtered.length}
          from={pagination.from}
          to={pagination.to}
          onPageChange={setPage}
        />
      </section>

      {ficheOpen && (
        <Modal
          title={fiche?.client ? `Fiche client - ${fiche.client.full_name}` : "Fiche client"}
          onClose={() => setFicheOpen(false)}
          footer={fiche?.client && (
            <>
              <button className="ghost-btn" type="button" onClick={previewPdf}><PictureAsPdf /> Visionner PDF</button>
              <button className="primary-btn" type="button" onClick={printPdf}><Print /> Imprimer</button>
            </>
          )}
        >
          {!fiche?.client ? (
            <EmptyState label="Chargement de la fiche..." />
          ) : (
            <div className="client-fiche" ref={ficheRef}>
              <div className="fiche-head">
                <div>
                  <span>OwSur Invest</span>
                  <h2>{fiche.client.full_name}</h2>
                  <p>{fiche.client.generated_client_id || fiche.client.client_number}</p>
                </div>
                {fiche.client.photo_client_url && <img src={assetUrl(fiche.client.photo_client_url)} alt="Client" />}
              </div>

              <div className="fiche-grid">
                <section>
                  <h3>Identification</h3>
                  <dl>
                    <dt>Nom complet</dt><dd>{fiche.client.full_name}</dd>
                    <dt>Sexe</dt><dd>{fiche.client.sex || "-"}</dd>
                    <dt>Telephone</dt><dd>{fiche.client.phone || "-"}</dd>
                    <dt>Adresse</dt><dd>{fiche.client.address || "-"} {fiche.client.commune || ""}</dd>
                    <dt>Ville</dt><dd>{fiche.client.city_name || "-"}</dd>
                    <dt>Agent</dt><dd>{fiche.client.commercial_agent_name || "-"}</dd>
                    <dt>Statut</dt><dd>{fiche.client.client_status || "-"}</dd>
                  </dl>
                </section>
                <section>
                  <h3>Situation economique</h3>
                  <dl>
                    <dt>Activite principale</dt><dd>{fiche.client.main_activity || "-"}</dd>
                    <dt>Revenu journalier</dt><dd>{money(fiche.client.estimated_daily_revenue, "CDF")}</dd>
                    <dt>Revenu mensuel</dt><dd>{money(fiche.client.estimated_monthly_revenue, "CDF")}</dd>
                    <dt>Charges mensuelles</dt><dd>{money(fiche.client.estimated_monthly_charges, "CDF")}</dd>
                    <dt>Fonds de roulement</dt><dd>{money(fiche.client.existing_working_capital, "CDF")}</dd>
                    <dt>Score risque</dt><dd>{fiche.client.risk_score || 0}</dd>
                  </dl>
                </section>
              </div>

              <section className="fiche-section">
                <h3>Activites liees</h3>
                {fiche.activities?.length ? (
                  <div className="fiche-tags">{fiche.activities.map((activity) => <span key={activity.id}>{activity.activity_name}</span>)}</div>
                ) : <p>Aucune activite cataloguee.</p>}
              </section>

              <section className="fiche-section">
                <h3>Pieces jointes</h3>
                <div className="fiche-docs">
                  {[
                    ["Photo client", fiche.client.photo_client_url],
                    ["Piece d'identite", fiche.client.identity_card_url],
                    ["Photo activite", fiche.client.business_photo_url],
                  ].map(([label, url]) => (
                    <div key={label}>
                      <span>{label}</span>
                      {url ? <img src={assetUrl(url)} alt={label} /> : <small>Non chargee</small>}
                    </div>
                  ))}
                </div>
              </section>

              <section className="fiche-section">
                <h3>Investissements</h3>
                {fiche.investments?.length ? (
                  <table className="fiche-table">
                    <thead><tr><th>ID</th><th>Produit</th><th>Brut</th><th>Net</th><th>Recouvre</th><th>Statut</th></tr></thead>
                    <tbody>{fiche.investments.map((investment) => (
                      <tr key={investment.id}>
                        <td>{investment.collection_card_id || investment.reference}</td>
                        <td>{investment.product_name}</td>
                        <td>{money(investment.subscription_amount || investment.amount_invested, investment.currency)}</td>
                        <td>{money(investment.net_disbursement_amount, investment.currency)}</td>
                        <td>{money(investment.amount_recovered, investment.currency)} ({percent(investment.recovery_rate)})</td>
                        <td>{investment.status}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <p>Aucun investissement.</p>}
              </section>

              <section className="fiche-section">
                <h3>Recouvrements</h3>
                {fiche.recoveries?.length ? (
                  <table className="fiche-table">
                    <thead><tr><th>Date</th><th>Produit</th><th>ID carte</th><th>Montant</th><th>Agent</th></tr></thead>
                    <tbody>{fiche.recoveries.map((recovery) => (
                      <tr key={recovery.id}>
                        <td>{dateOnly(recovery.paid_at)}</td>
                        <td>{recovery.product_name}</td>
                        <td>{recovery.collection_card_id || recovery.investment_reference}</td>
                        <td>{money(recovery.amount_paid, recovery.currency)}</td>
                        <td>{recovery.recovery_agent_name || "-"}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <p>Aucun recouvrement.</p>}
              </section>

              <section className="fiche-section">
                <h3>Echeancier et incidents</h3>
                <div className="fiche-grid">
                  <div>
                    <h4>Echeances</h4>
                    {fiche.schedules?.length ? (
                      <table className="fiche-table compact">
                        <thead><tr><th>Date</th><th>Attendu</th><th>Reste</th><th>Statut</th></tr></thead>
                        <tbody>{fiche.schedules.map((row) => (
                          <tr key={row.id}>
                            <td>{dateOnly(row.due_date)}</td>
                            <td>{money(row.expected_amount, row.currency)}</td>
                            <td>{money(row.remaining_amount, row.currency)}</td>
                            <td>
                              {row.status}
                              {String(row.status || "").toUpperCase() === "PAID" && row.payment_reference && (
                                <small className="payment-reference">Ref paiement : {row.payment_reference}</small>
                              )}
                            </td>
                          </tr>
                        ))}</tbody>
                      </table>
                    ) : <p>Aucun echeancier.</p>}
                  </div>
                  <div>
                    <h4>Incidents</h4>
                    {fiche.incidents?.length ? (
                      <table className="fiche-table compact">
                        <thead><tr><th>Type</th><th>Retard</th><th>Montant</th><th>Statut</th></tr></thead>
                        <tbody>{fiche.incidents.map((incident) => (
                          <tr key={incident.id}><td>{incident.incident_type}</td><td>{incident.days_late}</td><td>{money(incident.amount, incident.currency)}</td><td>{incident.status}</td></tr>
                        ))}</tbody>
                      </table>
                    ) : <p>Aucun incident.</p>}
                  </div>
                </div>
              </section>

              <section className="fiche-section">
                <h3>Historique audit</h3>
                {fiche.auditLogs?.length ? (
                  <table className="fiche-table compact">
                    <thead><tr><th>Date</th><th>Utilisateur</th><th>Action</th><th>Entite</th></tr></thead>
                    <tbody>{fiche.auditLogs.map((log) => (
                      <tr key={log.id}><td>{dateOnly(log.created_at)}</td><td>{log.user_name || "Systeme"}</td><td>{log.action}</td><td>{log.entity}</td></tr>
                    ))}</tbody>
                  </table>
                ) : <p>Aucune trace liee.</p>}
              </section>
            </div>
          )}
        </Modal>
      )}

      {modalOpen && (
        <Modal
          title={editing ? "Modifier le client" : "Ajouter un client"}
          onClose={() => setModalOpen(false)}
          footer={<button className="primary-btn" form="client-form"><Save /> Enregistrer</button>}
        >
          <form id="client-form" onSubmit={submit} encType="multipart/form-data">
            <div className="form-grid">
              <label>Nom<input value={form.last_name || ""} onChange={(e) => update("last_name", e.target.value)} required /></label>
              <label>Prenom<input value={form.first_name || ""} onChange={(e) => update("first_name", e.target.value)} required /></label>
              <label>Sexe<select value={form.sex || "M"} onChange={(e) => update("sex", e.target.value)}><option value="M">Masculin</option><option value="F">Feminin</option><option value="AUTRE">Autre</option></select></label>
              <label>Telephone<input value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} /></label>
              <label className="full-row">Adresse<input value={form.address || ""} onChange={(e) => update("address", e.target.value)} /></label>
              <label>Commune<input value={form.commune || ""} onChange={(e) => update("commune", e.target.value)} /></label>
              <label>Revenu journalier<input type="number" value={form.estimated_daily_revenue || 0} onChange={(e) => update("estimated_daily_revenue", e.target.value)} /></label>
              <label>Revenu mensuel auto<input type="number" value={monthlyRevenue} readOnly /><span className="field-hint">Calcul : revenu journalier x 26 jours ouvrables</span></label>
              <label>Charges mensuelles<input type="number" value={form.estimated_monthly_charges || 0} onChange={(e) => update("estimated_monthly_charges", e.target.value)} /></label>
              <label>Fonds de roulement<input type="number" value={form.existing_working_capital || 0} onChange={(e) => update("existing_working_capital", e.target.value)} /></label>
              <label>Score de risque /10<input type="number" min="0" max="10" value={form.risk_score || 0} onChange={(e) => update("risk_score", e.target.value)} /><span className="field-hint">Appreciation de l'agent de developpement</span></label>

              <div className="full-row activity-picker">
                <div className="activity-picker__head">
                  <span>Activites du client</span>
                  <div className="activity-picker__add">
                    <input value={newActivity} onChange={(e) => setNewActivity(e.target.value)} placeholder="Nouvelle activite" />
                    <button className="ghost-btn" type="button" onClick={addActivity}><Add /> Catalogue</button>
                  </div>
                </div>
                <div className="activity-options">
                  {activityCatalog.map((activity) => (
                    <label className="check-card" key={activity.id}>
                      <input
                        type="checkbox"
                        checked={form.activity_ids.includes(String(activity.id))}
                        onChange={() => toggleActivity(activity.id)}
                      />
                      <span>{activity.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <label className="file-field">Photo client<input type="file" accept="image/*" onChange={(e) => update("photo_client", e.target.files?.[0] || null)} /><span>{form.photo_client?.name || form.photo_client_url || "Aucun fichier"}</span></label>
              <label className="file-field">Photo activite<input type="file" accept="image/*" onChange={(e) => update("business_photo", e.target.files?.[0] || null)} /><span>{form.business_photo?.name || form.business_photo_url || "Aucun fichier"}</span></label>
              <label className="file-field full-row">Piece d'identite<input type="file" accept="image/*" onChange={(e) => update("identity_card", e.target.files?.[0] || null)} /><span>{form.identity_card?.name || form.identity_card_url || "Aucun fichier"}</span></label>
            </div>
          </form>
        </Modal>
      )}
    </main>
  );
}

export default ClientsPage;
